import { forwardRef, useState, useEffect, useMemo } from 'react';
import { DataGrid, type DataGridProps } from '@mui/x-data-grid';
import { type SxProps, type Theme } from '@mui/material/styles';

// ==============================|| CUSTOM DATA GRID WITH DYNAMIC X SCROLL ||============================== //

interface DataGridWithDynamicXScrollProps extends Omit<DataGridProps, 'sx'> {
  sx?: SxProps<Theme>;
}

const DataGridWithDynamicXScroll = forwardRef<HTMLDivElement, DataGridWithDynamicXScrollProps>(({ sx = {}, ...others }, ref) => {
  const { rows, loading } = others;
  const hasRows = Boolean(rows?.length);

  const useIsInViewport = (el: Element | null) => {
    const [isIntersecting, setIsIntersecting] = useState(false);

    const observer = useMemo(
      () =>
        new IntersectionObserver(([entry]) => {
          setIsIntersecting(entry.isIntersecting);
        }),
      []
    );

    useEffect(() => {
      if (el) {
        observer.observe(el);
      }

      return () => {
        observer.disconnect();
      };
    }, [el, observer]);

    return isIntersecting;
  };

  const [footerElement, setFooterElement] = useState<Element | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const footer = document.getElementsByClassName('MuiDataGrid-footerContainer')[0];
      setFooterElement(footer || null);
    }, 100); // Delay of 100ms to allow rendering to stabilize

    return () => {
      clearTimeout(timeout);
    };
  }, [rows]); // Ensure footerElement is set after rows are rendered

  const isDataGridFooterInViewport = useIsInViewport(footerElement);

  return (
    <DataGrid
      ref={ref}
      sx={{
        '& .MuiDataGrid-main': {
          transform: `rotateX(${isDataGridFooterInViewport || !hasRows ? 0 : 180}deg)`
        },
        '& .MuiDataGrid-virtualScroller': {
          transform: `rotateX(${isDataGridFooterInViewport || !hasRows ? 0 : 180}deg)`
        },
        '& .MuiDataGrid-scrollbar': {
          position: isDataGridFooterInViewport || !hasRows ? 'absolute !important' : 'unset !important'
        },
        ...sx
      }}
      {...others}
      hideFooter={Boolean(loading)}
    />
  );
});
DataGridWithDynamicXScroll.displayName = 'DataGridWithDynamicXScroll';

export default DataGridWithDynamicXScroll;
